import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  CheckIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

interface ShoppingItem {
  id: string;
  name: string;
  quantity: number;
  unit: string | null;
  estimated_price: number | null;
  is_checked: boolean;
  checked_by_name: string | null;
  category: string | null;
  notes: string | null;
  added_by_name: string | null;
  added_by_avatar: string | null;
}

interface ShoppingListDetail {
  id: string;
  name: string;
  color: string;
  store_name: string | null;
  items: ShoppingItem[];
  total_items: number;
  checked_items: number;
  estimated_total: number | null;
}

export default function ShoppingListDetail() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState('');

  const { register, handleSubmit, reset } = useForm<{ name: string; quantity: number; unit: string }>();

  const { data: list, isLoading } = useQuery({
    queryKey: ['shoppingList', listId],
    queryFn: async () => {
      const response = await api.get(`/shopping/${listId}`);
      return response.data as ShoppingListDetail;
    },
    enabled: !!listId,
  });

  const addMutation = useMutation({
    mutationFn: async (data: { name: string; quantity?: number; unit?: string }) => {
      const response = await api.post(`/shopping/${listId}/items`, {
        ...data,
        quantity: data.quantity || 1,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shoppingList', listId] });
      queryClient.invalidateQueries({ queryKey: ['shoppingLists'] });
      toast.success('Item added!');
      setShowAddModal(false);
      setNewItem('');
      reset();
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_checked }: { id: string; is_checked: boolean }) => {
      await api.patch(`/shopping/items/${id}`, { is_checked });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shoppingList', listId] });
      queryClient.invalidateQueries({ queryKey: ['shoppingLists'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/shopping/items/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shoppingList', listId] });
      queryClient.invalidateQueries({ queryKey: ['shoppingLists'] });
      toast.success('Item removed');
    },
  });

  const handleQuickAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    addMutation.mutate({ name: newItem.trim() });
  };

  const uncheckedItems = list?.items.filter((item) => !item.is_checked) || [];
  const checkedItems = list?.items.filter((item) => item.is_checked) || [];

  if (isLoading) {
    return (
      <div className="page-content flex items-center justify-center min-h-[50vh]">
        <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/shopping')} className="btn-icon">
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{list?.name}</h1>
          {list?.store_name && <p className="text-surface-400">{list.store_name}</p>}
        </div>
      </div>

      {/* Progress */}
      {list && list.total_items > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-surface-400">{list.checked_items} of {list.total_items} items</span>
            <span className="text-white font-medium">
              {Math.round((list.checked_items / list.total_items) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(list.checked_items / list.total_items) * 100}%` }}
              className="h-full rounded-full"
              style={{ backgroundColor: list.color }}
            />
          </div>
        </div>
      )}

      {/* Quick Add */}
      <form onSubmit={handleQuickAdd} className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="Add item..."
            className="input flex-1"
          />
          <button type="submit" disabled={!newItem.trim()} className="btn-primary">
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
      </form>

      {/* Items */}
      {list?.items.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <h2 className="text-xl font-bold text-white mb-2">List is empty</h2>
          <p className="text-surface-400">Add some items to your shopping list</p>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {/* Unchecked Items */}
          {uncheckedItems.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-surface-400 mb-3">To Buy</h3>
              <div className="space-y-2">
                {uncheckedItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="card flex items-center gap-3 py-2 px-3"
                  >
                    <button
                      onClick={() => toggleMutation.mutate({ id: item.id, is_checked: true })}
                      className="w-5 h-5 rounded border-2 border-surface-500 hover:border-emerald-500 transition-colors flex items-center justify-center flex-shrink-0"
                    />
                    {item.added_by_avatar ? (
                      <img
                        src={item.added_by_avatar}
                        alt={item.added_by_name || ''}
                        className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-surface-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] text-surface-300">
                          {item.added_by_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{item.name}</span>
                        {item.quantity > 1 && (
                          <span className="text-xs text-surface-400">
                            × {item.quantity} {item.unit}
                          </span>
                        )}
                      </div>
                      {item.notes && (
                        <p className="text-xs text-surface-400">{item.notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteMutation.mutate(item.id)}
                      className="btn-icon text-surface-500 hover:text-red-400"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Checked Items */}
          {checkedItems.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-surface-400 mb-3">
                Completed ({checkedItems.length})
              </h3>
              <div className="space-y-2">
                {checkedItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="card flex items-center gap-3 py-2 px-3 opacity-60"
                  >
                    <button
                      onClick={() => toggleMutation.mutate({ id: item.id, is_checked: false })}
                      className="w-5 h-5 rounded bg-emerald-500 flex items-center justify-center text-white flex-shrink-0"
                    >
                      <CheckIcon className="w-3 h-3" />
                    </button>
                    {item.added_by_avatar ? (
                      <img
                        src={item.added_by_avatar}
                        alt={item.added_by_name || ''}
                        className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-surface-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] text-surface-300">
                          {item.added_by_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-white line-through">{item.name}</span>
                    </div>
                    <button
                      onClick={() => deleteMutation.mutate(item.id)}
                      className="btn-icon text-surface-500 hover:text-red-400"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

